import { StrictMode, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppHeader } from './components/AppHeader';
import { AppSidebar } from './components/AppSidebar';
import { AppFooter } from './components/AppFooter';
import { AuthProvider, isAdmin, isStaff, useAuth } from './hooks/useAuth';
import { BahasaProvider, useBahasa } from './i18n';
import { AboutPage } from './pages/AboutPage';
import { AdminPage } from './pages/AdminPage';
import { UsersPage } from './pages/UsersPage';
import { CamerasPage } from './pages/CamerasPage';
import { ProfilePage } from './pages/ProfilePage';
import { DashboardPage } from './pages/DashboardPage';
import { DashboardsPage } from './pages/DashboardsPage';
import { LoginPage } from './pages/LoginPage';
import { LogsPage } from './pages/LogsPage';
import { MonitorsPage } from './pages/MonitorsPage';
import { StatusPage } from './pages/StatusPage';
import { VideosPage } from './pages/VideosPage';
import { MultiVideosPage } from './pages/MultiVideosPage';
import { pasangMuatUlangAndroid } from './lib/muatUlangAndroid';
import './styles/index.css';
import { CameraImportPage } from './pages/CameraImportPage';
import { DownloadsPage } from './pages/DownloadsPage';

/**
 * Gerbang auth. Selama `loading` belum selesai kita BELUM tahu apakah ada sesi
 * — pemeriksaan /auth/me juga yang memicu pemulihan lewat cookie "Ingat saya"
 * (§F4.2). Melempar ke /login lebih awal akan membuat sesi 30 hari yang sah
 * terlihat seperti sudah kedaluwarsa di setiap reload.
 */
function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { t } = useBahasa();
  const [navOpen, setNavOpen] = useState(false);
  if (loading) {
    return (
      <p className="p-8 text-sm text-muted" role="status">
        {t('Checking your session…')}
      </p>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    /*
     * `h-screen` + `overflow-hidden`, bukan `min-h-screen`.
     *
     * Sebelumnya tinggi halaman = header (61px nyata) + konten yang dipatok
     * `calc(100vh - 3.25rem)` (52px) — selisih 9px yang memunculkan bilah
     * gulung tipis di setiap halaman, dan paling terasa saat layar penuh:
     * gambar terlihat seolah lebih besar daripada layar. Sekarang tinggi
     * shell-nya persis satu layar dan yang menggulung adalah `<main>`, jadi
     * tinggi header tidak perlu ditebak di tempat lain mana pun.
     */
    <div className="flex h-screen overflow-hidden bg-page">
      {/* Sidebar §G9. */}
      <AppSidebar open={navOpen} onClose={() => setNavOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Link lewati: harus jadi elemen fokus PERTAMA, karena sidebar berisi
            enam tautan yang berulang di setiap halaman. */}
        <a
          href="#main"
          className="sr-only rounded-md bg-surface px-3 py-2 text-sm focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-30"
        >
          {t('Skip to content')}
        </a>
        <AppHeader onOpenNav={() => setNavOpen(true)} />
        <main id="main" tabIndex={-1} className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          {children}
        </main>
        {/* Kaki halaman di luar `<main>`, bukan di dalamnya: di dalam ia ikut
            menggulung, dan di halaman sepanjang /logs itu berarti tulisan hak
            ciptanya cuma terlihat sesudah menggulung 96 baris tabel. */}
        <AppFooter />
      </div>
    </div>
  );
}

/**
 * Gerbang admin. Viewer yang mengetik /admin langsung dilempar ke Dashboard —
 * §G9 mensyaratkan menu admin disembunyikan sepenuhnya dari Viewer, bukan
 * sekadar di-disable, dan itu berlaku juga untuk rutenya. Ini kenyamanan UI,
 * bukan pengaman: yang menjaga data tetap `requireAdmin` di server (§F4).
 */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin(user)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Gerbang admin **atau** operator (§F8).
 *
 * Halaman yang menyusun — Users dan Dashboard List — dibuka untuk operator,
 * karena menugaskan kamera ke orang justru pekerjaannya. Yang membedakan
 * keduanya bukan halamannya melainkan tombol di dalamnya: hapus tetap milik
 * admin, dan itu dijaga server (§F8), bukan oleh gerbang ini.
 *
 * Ikut memulangkan sesi yang sedang dalam User view (§F8.1) — `isStaff` sudah
 * memperhitungkannya, jadi mode itu benar-benar terasa seperti akun biasa dan
 * bukan seperti admin dengan menu yang disembunyikan.
 */
function StaffOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!isStaff(user)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <LoginPage />;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route
        path="/dashboard"
        element={
          <Protected>
            <DashboardPage />
          </Protected>
        }
      />
      <Route
        path="/videos"
        element={
          <Protected>
            <VideosPage />
          </Protected>
        }
      />
      {/* Sebelum `/videos/:cameraId` secara peringkat: segmen tetap menang atas
          segmen dinamis di react-router v6, jadi `multi` tidak pernah terbaca
          sebagai id kamera. */}
      <Route
        path="/videos/multi"
        element={
          <Protected>
            <MultiVideosPage />
          </Protected>
        }
      />
      <Route
        path="/videos/:cameraId"
        element={
          <Protected>
            <VideosPage />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected>
            <AdminOnly>
              <AdminPage />
            </AdminOnly>
          </Protected>
        }
      />
      <Route
        path="/users"
        element={
          <Protected>
            <StaffOnly>
              <UsersPage />
            </StaffOnly>
          </Protected>
        }
      />
      {/* Bulk Import (§G11.3). Didaftarkan SEBELUM `/cameras/:id` supaya
          "import" tidak pernah dibaca sebagai id kamera. */}
      {/* Download Center (§G12) — semua peran, isinya milik masing-masing. */}
      <Route
        path="/downloads"
        element={
          <Protected>
            <DownloadsPage />
          </Protected>
        }
      />
      <Route
        path="/cameras/import"
        element={
          <Protected>
            <StaffOnly>
              <CameraImportPage />
            </StaffOnly>
          </Protected>
        }
      />
      {/* Dashboard punya halamannya sendiri: isinya kamera + siapa yang boleh
          melihatnya, dan itu bukan urusan halaman User (§C2.1). */}
      <Route
        path="/dashboards"
        element={
          <Protected>
            <StaffOnly>
              <DashboardsPage />
            </StaffOnly>
          </Protected>
        }
      />
      {/* Custom Monitors (§F9) — milik pemakai sendiri, jadi tidak ada gerbang
          peran apa pun di sini. Justru viewer yang paling memerlukannya. */}
      <Route
        path="/monitors"
        element={
          <Protected>
            <MonitorsPage />
          </Protected>
        }
      />
      <Route
        path="/logs"
        element={
          <Protected>
            {/* Menyembunyikan menunya saja tidak cukup: halaman yang hilang
                dari navigasi tapi tetap terbuka lewat URL adalah pagar yang
                cuma terlihat seperti pagar. Yang ini menutupnya sungguhan
                di klien — pagar sebenarnya tetap di server (§F8). */}
            <StaffOnly>
              <LogsPage />
            </StaffOnly>
          </Protected>
        }
      />
      <Route
        path="/cameras"
        element={
          <Protected>
            <CamerasPage />
          </Protected>
        }
      />
      {/* Status itu halaman diagnostik sistem — sekelas `/admin`, bukan beranda.
          Viewer tidak punya urusan dengannya, dan `AdminOnly` memulangkan mereka
          ke Dashboard. */}
      <Route
        path="/status"
        element={
          <Protected>
            <AdminOnly>
              <StatusPage />
            </AdminOnly>
          </Protected>
        }
      />
      {/* Profil sendiri — semua peran, termasuk Viewer (§F7). Ini satu-satunya
          halaman tempat orang boleh menulis ke barisnya sendiri, jadi ia berdiri
          di luar `AdminOnly`. */}
      <Route
        path="/profile"
        element={
          <Protected>
            <ProfilePage />
          </Protected>
        }
      />
      {/* Beranda = Dashboard (§C2). Sebelumnya `/` menampilkan Status, jadi tiap
          orang mendarat di halaman diagnostik alih-alih di gambar kameranya. */}
      {/* About (§G15) — semua peran, dan sengaja tanpa satu pun angka sistem:
          yang itu urusan /status. */}
      <Route
        path="/about"
        element={
          <Protected>
            <AboutPage />
          </Protected>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/*
 * Perundingan tarik-untuk-muat-ulang dengan APK Android (§G8, 31 Agustus 2026).
 * Di browser biasa ia tidak melakukan apa pun; di APK ia yang menjawab
 * "boleh muat ulang sekarang?" — lihat `lib/muatUlangAndroid.ts`.
 */
pasangMuatUlangAndroid();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Bahasa di luar auth (§H9): halaman login pun harus bisa berbahasa
        Indonesia, dan pilihannya ada di perangkat — bukan di akun — jadi ia
        tidak menunggu siapa pun masuk. */}
    <BahasaProvider>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </BahasaProvider>
  </StrictMode>,
);
